// Правни и финанси (04 §2) — who the company legally is.
//
// This tab is where a wrong answer costs the most: the owner reads a name off it and says it out
// loud to whoever picks up. So it shows its own confidence, prominently, and it never presents a
// match below the 06 §3 bar as if it were settled.
//
// "регистър към DD.MM.YYYY" is not decoration either. The register is a snapshot; a manager who
// resigned last week is still in a three-week-old dump, and the date is the only thing that tells
// the owner how much to trust the name in front of them.

import { useState } from 'react';
import { useApplyManualEntity, useLegalEntity, type LegalEntity } from './useActivity.js';

const FORM_LABELS: Record<string, string> = {
  ET: 'ЕТ',
  EOOD: 'ЕООД',
  OOD: 'ООД',
  AD: 'АД',
  EAD: 'ЕАД',
  DZZD: 'ДЗЗД',
  other: 'друга форма',
};

/** 02 §1: CONFIRMED_CONFIDENCE = 80. Below that the match is shown as a suggestion. */
const CONFIDENT = 80;

function People({ title, names }: { title: string; names: string[] }) {
  if (names.length === 0) return null;
  return (
    <div className="legal__people">
      <dt>{title}</dt>
      <dd>
        {names.map((name) => (
          <span key={name} className="legal__person">
            👤 {name}
          </span>
        ))}
      </dd>
    </div>
  );
}

function Fact({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string | null;
  mono?: boolean;
}) {
  if (!value) return null;
  return (
    <div className="legal__fact-card">
      <dt>{label}</dt>
      <dd className={mono ? 'legal__eik' : undefined}>{value}</dd>
    </div>
  );
}

function Facts({ entity }: { entity: LegalEntity }) {
  const seat =
    entity.seat_city === null
      ? (entity.registered_seat ?? null)
      : `${entity.seat_city}${entity.registered_seat ? `, ${entity.registered_seat}` : ''}`;

  return (
    <dl className="legal__facts">
      <Fact label="ЕИК" value={entity.eik} mono />
      <Fact label="Състояние" value={entity.company_status} />
      <Fact label="Регистрация" value={entity.registration_date ?? null} />
      <Fact label="Седалище" value={seat} />
      <People title="Собственици" names={entity.owner_names ?? []} />
      <People title="Управители" names={entity.manager_names ?? []} />
    </dl>
  );
}

function ManualEikForm({ leadId }: { leadId: number }) {
  const [eik, setEik] = useState('');
  const apply = useApplyManualEntity();
  const valid = /^\d{9}(\d{4})?$/.test(eik.trim());

  return (
    <form
      className="legal__manual"
      onSubmit={(e) => {
        e.preventDefault();
        if (!valid) return;
        apply.mutate(
          { leadId, eik: eik.trim() },
          {
            onSuccess: () => setEik(''),
          },
        );
      }}
    >
      <div className="legal__manual-label">
        <strong>Ръчно свързване по ЕИК</strong>
        <span>Въведете 9 или 13 цифри за директно свързване с Търговския регистър</span>
      </div>
      <div className="legal__manual-controls">
        <input
          type="text"
          className="filter-input"
          placeholder="Ръчен ЕИК (9 или 13 цифри)"
          value={eik}
          onChange={(e) => setEik(e.target.value)}
        />
        <button
          type="submit"
          className="btn btn--primary btn--sm"
          disabled={!valid || apply.isPending}
        >
          {apply.isPending ? 'Запис…' : 'Приложи ЕИК'}
        </button>
      </div>
      {apply.error ? <span className="legal__err">{apply.error.message}</span> : null}
    </form>
  );
}

function FinancialsTable({ financials }: { financials: NonNullable<LegalEntity['financials']> }) {
  if (financials.length === 0) return null;
  return (
    <div className="legal__financials">
      <h4 className="legal__subhead">Финансови показатели</h4>
      <div className="tbl__wrap">
        <table className="tbl">
          <thead>
            <tr>
              <th>Година</th>
              <th className="tbl__num">Приходи (лв.)</th>
              <th className="tbl__num">Печалба (лв.)</th>
              <th className="tbl__num">Служители</th>
            </tr>
          </thead>
          <tbody>
            {financials.map((f) => (
              <tr key={f.year}>
                <td style={{ fontWeight: 700 }}>{f.year}</td>
                <td className="tbl__num">
                  {f.revenue_bgn !== null ? f.revenue_bgn.toLocaleString('bg-BG') : '—'}
                </td>
                <td className="tbl__num">
                  {f.profit_bgn !== null ? f.profit_bgn.toLocaleString('bg-BG') : '—'}
                </td>
                <td className="tbl__num">{f.employees ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LegalFooter({ entity }: { entity: LegalEntity }) {
  const asOf = entity.registry_dump_date
    ? new Date(entity.registry_dump_date).toLocaleDateString('bg-BG')
    : null;
  const parts = [
    asOf ? `регистър към ${asOf}` : 'без дата на регистъра',
    entity.match_method,
    entity.source,
  ].filter(Boolean);
  return <footer className="legal__foot">{parts.join(' · ')}</footer>;
}

export function LegalTab({
  leadId,
  parentLeadId,
}: {
  leadId: number;
  parentLeadId?: number | null;
}) {
  const { data: entity, isLoading, error } = useLegalEntity(leadId, parentLeadId);

  if (isLoading) return <p className="legal__msg">Зареждане…</p>;
  if (error) return <p className="legal__caution">Грешка при зареждане: {error.message}</p>;
  if (!entity) {
    return (
      <div className="legal">
        <p className="legal__msg">
          Няма съвпадение в Търговския регистър. Обектът може да е свободна практика, да работи под
          друго име, или индексът да не е зареден.
        </p>
        <ManualEikForm leadId={leadId} />
      </div>
    );
  }

  const confidence = entity.match_confidence ?? 0;
  const settled = confidence >= CONFIDENT;
  const formLabel = entity.legal_form
    ? (FORM_LABELS[entity.legal_form] ?? entity.legal_form)
    : null;

  return (
    <section className="legal">
      <header className="legal__head">
        <h3 className="legal__name">
          {entity.legal_name ?? '—'}{' '}
          {formLabel ? <span className="legal__form">{formLabel}</span> : null}
        </h3>
        <div className="legal__badges">
          {entity.is_from_parent ? (
            <span className="badge badge--neutral">от основния обект</span>
          ) : null}
          <span className={`tag ${settled ? 'tag--ok' : 'tag--warn'}`}>
            {settled ? `съвпадение ${confidence}%` : `несигурно съвпадение ${confidence}%`}
          </span>
        </div>
      </header>

      {!settled ? (
        <p className="legal__caution">
          Не използвайте имената по-долу в разговора, докато не потвърдите фирмата.
        </p>
      ) : null}

      <Facts entity={entity} />
      <FinancialsTable financials={entity.financials ?? []} />
      <ManualEikForm leadId={leadId} />
      <LegalFooter entity={entity} />
    </section>
  );
}
